<script setup lang="ts">
/**
 * FormKit Number Component
 *
 * Supports different modes:
 * - currency: For monetary values (divides by 100, shows 2 decimal places)
 * - dimension: For measurements in cm (divides by 10, shows 1 decimal place)
 * - weight: For weight values in kg (divides by 1000, shows 3 decimal places)
 * - identifier: For ID fields like Product ID (disables thousand separators to prevent formatting issues when copying)
 *
 * Props:
 * - useGrouping: Can be set to false to manually disable thousand separators
 */
import type { FormKitFrameworkContext } from '@formkit/core'
import InputNumber from 'primevue/inputnumber'

const props = defineProps<{
  context: FormKitFrameworkContext
}>()

const isCurrency = computed(() => props.context.mode === 'currency')
const isDimension = computed(() => props.context.mode === 'dimension')
const isWeight = computed(() => props.context.mode === 'weight')
const isIdentifier = computed(() => props.context.mode === 'identifier')
const nullInput = computed(
  () => props.context.attrs?.props?.nullValues === true,
)

// Disable grouping for identifier fields (like Product ID) to prevent spaces in numbers
const useGrouping = computed(() => {
  if (isIdentifier.value) return false
  // Check if explicitly disabled via props
  if (props.context.attrs?.props?.useGrouping === false) return false
  return true
})

const root = props.context.node.at('$root')
const config = props.context as any
const disabled = computed(() => {
  return (
    props.context.disabled ?? config.attrs.watchDisabled?.(root.value) ?? false
  )
})

const value = computed(() => {
  if (isDimension.value) {
    return (props.context._value ?? 0) / 10
  }
  if (isCurrency.value) {
    return +((props.context._value ?? 0) / 100).toFixed(2)
  }
  if (isWeight.value) {
    return (props.context._value ?? 0) / 1000
  }
  return nullInput.value && props.context._value === null
    ? null
    : props.context._value
})

const handleValue = (value) => {
  if (isDimension.value) {
    return +value * 10
  }
  if (isCurrency.value) {
    return +(value * 100).toFixed(2)
  }
  if (isWeight.value) {
    return +value * 1000
  }
  return nullInput && value === null ? null : +value
}

function handleInput(value) {
  if (disabled.value) return
  props.context.node.input(handleValue(value))
}
</script>

<template>
  <InputNumber
    :model-value="value"
    :disabled="disabled"
    mode="decimal"
    :useGrouping="useGrouping"
    class="h-10 w-full"
    :class="context.attrs.classes"
    :input-style="{
      border: '0 !important',
      'font-size': '0.875rem !important',
      color: 'rgb(55, 65, 81) !important',
      width: '150px',
    }"
    :min-fraction-digits="isCurrency ? 2 : isDimension ? 1 : isWeight ? 3 : 0"
    @update:model-value="handleInput"
  />
</template>
